import { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Modal } from "react-bootstrap";
import DeviceTableTab from "./view_data/DeviceTableTab";
import DeviceExportTab from "./view_data/DeviceExportTab";
import DeviceStatExportTab from "./view_data/DeviceStatExportTab";
import http from "services/httpClient";

interface DeviceLabel {
  column_index: number;
  label: string;
  is_enabled: boolean;
  multiplier?: number;
  offset?: number;
  unit?: string;
}

interface DeviceStatDefinition {
  id: number;
  device_id: number;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: number;
  result_label: string;
  unit?: string;
}

interface ViewDataModalProps {
  show: boolean;
  onHide: () => void;
  deviceId: number;
  deviceName: string;
}

export function ViewDataModal({ show, onHide, deviceId, deviceName }: ViewDataModalProps) {
  const [labels, setLabels] = useState<DeviceLabel[]>([]);
  const [stats, setStats] = useState<DeviceStatDefinition[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (!show || !deviceId) return;

    const fetchData = async () => {
      setLoadingLabels(true);
      setLoadingStats(true);

      try {
        const [rawRes, calcRes, statRes] = await Promise.all([
          http.get(`/device/${deviceId}/labels`),
          http.get(`/calculated_params/${deviceId}`),
          http.get(`/stat-definitions/device/${deviceId}`), // adjust route if your backend uses another path
        ]);

        const rawLabels: DeviceLabel[] = rawRes.data || [];

        const calcLabels: DeviceLabel[] = (calcRes.data || []).map((item: any) => ({
          column_index: item.column_index,
          label: item.result_label,
          is_enabled: true,
          multiplier: 1,
          offset: 0,
          unit: item.unit || "",
        }));

        setLabels([...rawLabels, ...calcLabels]);
        setStats(statRes.data || []);
      } catch {
        setLabels([]);
        setStats([]);
      } finally {
        setLoadingLabels(false);
        setLoadingStats(false);
      }
    };

    fetchData();
  }, [show, deviceId]);

  const labelMetaByIndex = useMemo(
    () => new Map<number, DeviceLabel>(labels.map((l) => [l.column_index, l])),
    [labels]
  );

  const hasStats = stats.length > 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Device Data Viewer</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "400px", maxHeight: "75vh" }}>
        <Tabs defaultActiveKey="table" id="device-tabs">
          <Tab eventKey="table" title="Device Table">
            <DeviceTableTab
              deviceId={deviceId}
              deviceName={deviceName}
              labels={labels}
              labelMetaByIndex={labelMetaByIndex}
              loadingLabels={loadingLabels}
            />
          </Tab>

          <Tab eventKey="export" title="Export CSV">
            <DeviceExportTab deviceId={deviceId} deviceName={deviceName} />
          </Tab>

          <Tab
            eventKey="stat-export"
            title="Export Stat CSV"
            disabled={loadingStats || !hasStats}
          >
            <DeviceStatExportTab
              deviceId={deviceId}
              deviceName={deviceName}
            />
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
}

export default ViewDataModal;